<script setup lang="ts">
import {
  DeliveryMethods,
  SetDeliveryMethodPayload,
} from '@/api/modules/basket/types'
import type { BasketPoint } from '@/types/api/api-v3/basket-points'

import BasketAddressesHall from '@/components/Basket/basket-addresses/BasketAddressesHall.vue'
import BasketAddressesPickup from '@/components/Basket/basket-addresses/BasketAddressesPickup.vue'

import type { AddressClient } from '@/api/modules/addresses/addresses.types'

type Props = {
  effectShadow?: boolean
  deliveryMethod: DeliveryMethods
  maxCountOfVisibleAddresses: number
  selectedAddress?: BasketPoint | AddressClient
  pointsList: BasketPoint[]
  listWithAnimation?: boolean
  withoutValidate?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits([
  'onChangeDeliveryMethod',
  'onSelectDeliveryCourier',
  'onSetSelectedAddress',
  'open-dialog-redirect-city',
])

const onDeliveryMethodChanged = () => emit('onChangeDeliveryMethod')

const onSelectDeliveryCourier = () => emit('onSelectDeliveryCourier')

const onOpenDialogRedirectCity = (basketPoint: BasketPoint) =>
  emit('open-dialog-redirect-city', basketPoint)

const setSelectedAddress = (value: SetDeliveryMethodPayload) => {
  emit('onSetSelectedAddress', value)
}

const isShowAddresses = (
  deliveryMethod: DeliveryMethods.PICKUP | DeliveryMethods.HALL,
) => props.deliveryMethod === deliveryMethod && props.pointsList?.length
</script>

<template>
  <div class="address-windows">
    <the-transition-height>
      <slot v-if="deliveryMethod === DeliveryMethods.COURIER" />

      <basket-addresses-pickup
        v-else-if="isShowAddresses(DeliveryMethods.PICKUP)"
        :selected-address="selectedAddress as BasketPoint | undefined"
        :max-count-of-visible-addresses="maxCountOfVisibleAddresses"
        :delivery-method="deliveryMethod"
        :effect-shadow="effectShadow"
        :pickup-points="pointsList"
        :list-with-animation="listWithAnimation"
        :without-validate="withoutValidate"
        @delivery-method-changed="onDeliveryMethodChanged"
        @select-delivery-courier="onSelectDeliveryCourier"
        @set-selected-address="setSelectedAddress"
        @open-dialog-redirect-city="onOpenDialogRedirectCity"
      />

      <basket-addresses-hall
        v-else-if="isShowAddresses(DeliveryMethods.HALL)"
        :selected-address="selectedAddress as BasketPoint | undefined"
        :max-count-of-visible-addresses="maxCountOfVisibleAddresses"
        :effect-shadow="effectShadow"
        :delivery-method="deliveryMethod"
        :hall-points="pointsList"
        :list-with-animation="listWithAnimation"
        :without-validate="withoutValidate"
        @delivery-method-changed="onDeliveryMethodChanged"
        @select-delivery-courier="onSelectDeliveryCourier"
        @set-selected-address="setSelectedAddress"
        @open-dialog-redirect-city="onOpenDialogRedirectCity"
      />

      <div v-else class="address-windows__empty">
        В этой области нет ресторанов
      </div>
    </the-transition-height>
  </div>
</template>

<style scoped lang="scss">
.address-windows {
  height: 100%;

  &__empty {
    font-size: 16px;
    font-weight: 800;
    line-height: 22px;
    color: var(--ui-secondary);
    word-wrap: break-word;
  }
}
</style>
